/**
 * Import contacts and organizations from CSV or Excel.
 *
 * THE REVIEW STEP IS THE FEATURE. Uploading stages the rows and writes nothing to your contacts;
 * committing is the only thing that creates them. That is what makes cancelling free — there is
 * nothing to unwind — and it is why every row can be inspected before it becomes a record you have
 * to clean up later.
 *
 * MATCHED ROWS DEFAULT TO SKIP. The reviewer opts IN to touching an existing contact, never out of
 * it. Auto-merging is the one behaviour that cannot be reviewed afterwards: it overwrites fields on
 * a contact somebody has been maintaining and the previous values are gone.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { tfApi } from '../api/tf_api'
import { useApp } from '../contexts/app_context'
import { PageHeader } from '../components/layout/app_shell'
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Spinner,
  Tag,
  formatShortDate,
} from '../components/ui/primitives'
import type {
  ImportBatch,
  ImportResolution,
  ImportRow,
  ImportRowStatus,
  ImportTarget,
} from '../types/tf_types'

const PEOPLE_FIELDS = [
  'FirstName', 'MiddleName', 'LastName', 'DisplayName', 'PrimaryEmail', 'SecondaryEmail',
  'PrimaryPhone', 'PhoneExtension', 'JobTitle', 'Department', 'OrganizationName', 'LinkedInPage',
  'RelationshipStrength', 'ContactStatus', 'LeadStatus', 'NextMove', 'NextFollowUpDate',
  'OwnerUserId', 'Priority', 'Notes',
  //  Columns a supplier or lead list carries about the COMPANY, on the person's row. Recorded
  //  against the organization rather than copied onto every contact there.
  'Website', 'Industry', 'Description', 'NaicsCode',
  'AddressLine1', 'City', 'State', 'PostalCode', 'Country',
  //  A procurement vehicle and its awarded rate — NOT a job title, however the source file
  //  labelled the column.
  'ContractVehicle', 'VehicleCategory', 'RatePercent', 'SourceList',
]
const ORGANIZATION_FIELDS = [
  'CompanyName', 'Website', 'LinkedInPage', 'Industry', 'Description', 'NaicsCode',
  'Headquarters', 'CompanySize', 'StrategicValue', 'NextMove', 'NextFollowUpDate',
  'AddressLine1', 'City', 'State', 'PostalCode', 'SourceList',
]

const STATUS_TONE: Record<ImportRowStatus, string> = {
  NEW: 'strong',
  MATCHED: 'developing',
  DUPLICATE: 'developing',
  INVALID: 'blocked',
  IMPORTED: 'strong',
}

const STATUS_MEANING: Record<ImportRowStatus, string> = {
  NEW: 'Not in your contacts. Will be created.',
  MATCHED: 'Already in your contacts. Skipped unless you choose otherwise.',
  DUPLICATE: 'Appears twice in this file.',
  INVALID: 'Cannot be imported as it stands.',
  IMPORTED: 'Created by this import.',
}

export function ImportsPage() {
  const { notify, notifyError } = useApp()
  const [batches, setBatches] = useState<ImportBatch[] | null>(null)
  const [batch, setBatch] = useState<ImportBatch | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<{ message: string; technical?: string } | null>(null)
  const [target, setTarget] = useState<ImportTarget>('PEOPLE')
  const [confirming, setConfirming] = useState<'commit' | 'cancel' | null>(null)
  const fileRef = useRef<HTMLInputElement | null>(null)

  const loadList = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setBatches((await tfApi.fetchImports()).Items)
    } catch (err: any) {
      setError({ message: err?.detail?.message ?? String(err), technical: err?.detail?.technical })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadList()
  }, [loadList])

  const upload = async (file: File) => {
    setBusy(true)
    try {
      const staged = await tfApi.uploadImport(file, target)
      setBatch(staged)
      notify({
        tone: 'ok',
        title: 'File staged for review',
        body: `${staged.TotalRows} rows read. Nothing has been added to your contacts yet.`,
      })
      await loadList()
    } catch (err) {
      notifyError('The file could not be read', err)
    } finally {
      setBusy(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const open = async (batchId: number) => {
    setBusy(true)
    try {
      setBatch(await tfApi.fetchImport(batchId))
    } catch (err) {
      notifyError('Could not open that import', err)
    } finally {
      setBusy(false)
    }
  }

  const remap = async (header: string, field: string) => {
    if (!batch) return
    setBusy(true)
    try {
      const mapping = { ...batch.Mapping }
      if (field) mapping[header] = field
      else delete mapping[header]
      //  Re-staged server-side: the mapping decides what every row MEANS, so changing it changes
      //  which rows are duplicates and which are invalid.
      setBatch(await tfApi.setImportMapping(batch.ImportBatchId, mapping))
    } catch (err) {
      notifyError('Could not change the mapping', err)
    } finally {
      setBusy(false)
    }
  }

  const setResolutions = async (resolutions: Record<number, ImportResolution>) => {
    if (!batch) return
    setBusy(true)
    try {
      setBatch(await tfApi.setImportResolutions(batch.ImportBatchId, resolutions))
    } catch (err) {
      notifyError('Could not update the selection', err)
    } finally {
      setBusy(false)
    }
  }

  const commit = async () => {
    if (!batch) return
    setConfirming(null)
    setBusy(true)
    try {
      const done = await tfApi.commitImport(batch.ImportBatchId)
      setBatch(done)
      notify({
        tone: done.Failed ? 'info' : 'ok',
        title: `${done.Created ?? 0} created`,
        body: done.Failed
          ? `${done.Failed} row${done.Failed === 1 ? '' : 's'} failed and are marked on the list below.`
          : 'The import is complete.',
      })
      await loadList()
    } catch (err) {
      notifyError('The import could not be committed', err)
    } finally {
      setBusy(false)
    }
  }

  const cancel = async () => {
    if (!batch) return
    setConfirming(null)
    setBusy(true)
    try {
      setBatch(await tfApi.cancelImport(batch.ImportBatchId))
      notify({ tone: 'ok', title: 'Import cancelled', body: 'Nothing was added to your contacts.' })
      await loadList()
    } catch (err) {
      notifyError('Could not cancel the import', err)
    } finally {
      setBusy(false)
    }
  }

  const rows = batch?.Rows ?? []
  const willImport = useMemo(() => rows.filter((r) => r.Resolution === 'IMPORT').length, [rows])
  const fields = batch?.Target === 'ORGANIZATIONS' ? ORGANIZATION_FIELDS : PEOPLE_FIELDS
  const reviewable = batch?.ImportStatus === 'REVIEWING' || batch?.ImportStatus === 'UPLOADED'

  return (
    <>
      <PageHeader
        crumb="Tidalfin / Data / Imports"
        title="Imports"
        description="Load contacts and organizations from a CSV or Excel file. Nothing reaches your contacts until you commit, so reviewing is real and cancelling is free."
        actions={
          batch ? (
            <button className="btn secondary" type="button" onClick={() => setBatch(null)}>
              <span className="material-symbols-outlined">arrow_back</span>All imports
            </button>
          ) : (
            <button className="btn secondary" type="button" onClick={() => void loadList()}>
              <span className="material-symbols-outlined">refresh</span>Refresh
            </button>
          )
        }
      />

      <div className="body-scroll">
        {error && (
          <ErrorState title="Could not load imports" message={error.message}
                      technical={error.technical} onRetry={loadList} />
        )}

        {/* ------------------------------------------------------------ upload */}
        {!batch && (
          <div className="card" style={{ marginBottom: 14 }}>
            <div className="card-head">
              <span className="card-title">
                <span className="material-symbols-outlined">upload_file</span>Upload a file
              </span>
            </div>
            <div className="card-body">
              <div className="quick-log" style={{ flexWrap: 'wrap' }}>
                <select
                  className="ie-select"
                  value={target}
                  aria-label="What the file contains"
                  onChange={(e) => setTarget(e.target.value as ImportTarget)}
                >
                  <option value="PEOPLE">People</option>
                  <option value="ORGANIZATIONS">Organizations</option>
                </select>
                <input
                  ref={fileRef}
                  type="file"
                  aria-label="Choose a CSV or Excel file"
                  accept=".csv,.tsv,.txt,.xlsx,.xlsm"
                  disabled={busy}
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) void upload(file)
                  }}
                  style={{ fontSize: 'var(--fs-sm)' }}
                />
                {busy && <Spinner />}
              </div>
              <p className="person-sub" style={{ marginTop: 10 }}>
                CSV, TSV and .xlsx. The first row is treated as the header. Columns are matched to
                fields automatically where the name is unambiguous — anything else is left blank for
                you to set, because a wrong automatic mapping looks correct in the preview.
              </p>
              <p className="person-sub" style={{ marginTop: 6 }}>
                Every value is read as text. A phone number with a leading zero and a long numeric
                id both survive that; type inference does not.
              </p>
            </div>
          </div>
        )}

        {/* ------------------------------------------------------------ history */}
        {!batch && (loading ? <Spinner /> : (batches?.length ?? 0) === 0 ? (
          <EmptyState icon="upload_file" title="No imports yet"
                      message="Upload a file above to get started." />
        ) : (
          <div className="table-wrap">
            <div className="table-scroll">
              <table className="dt">
                <caption className="sr-only">Previous imports with their row counts and state.</caption>
                <thead>
                  <tr>
                    <th>File</th><th>Contains</th><th>When</th><th>Rows</th>
                    <th>New</th><th>Matched</th><th>Invalid</th><th>State</th><th />
                  </tr>
                </thead>
                <tbody>
                  {batches!.map((b) => (
                    <tr key={b.ImportBatchId}>
                      <td><span className="person-name">{b.FileName}</span></td>
                      <td><Tag>{b.Target === 'ORGANIZATIONS' ? 'Organizations' : 'People'}</Tag></td>
                      <td><span className="person-sub">{formatShortDate(b.CreatedAt)}</span></td>
                      <td className="num">{b.TotalRows}</td>
                      <td className="num">{b.NewCount}</td>
                      <td className="num">{b.MatchedCount + b.DuplicateCount}</td>
                      <td className="num">{b.InvalidCount}</td>
                      <td>
                        <span className={`badge b-${
                          b.ImportStatus === 'COMMITTED' ? 'strong'
                          : b.ImportStatus === 'CANCELLED' ? 'dormant' : 'developing'}`}>
                          <span className="dot" />{b.ImportStatus}
                        </span>
                      </td>
                      <td>
                        <button className="btn secondary sm" type="button"
                                onClick={() => void open(b.ImportBatchId)}>
                          {b.ImportStatus === 'COMMITTED' ? 'View' : 'Review'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="table-foot"><span>{batches!.length} imports</span></div>
          </div>
        ))}

        {/* ------------------------------------------------------------ review */}
        {batch && (
          <>
            <div className="imp-summary">
              <div className="imp-stat">
                <span className="n">{batch.TotalRows}</span>
                <span className="l">Rows read</span>
              </div>
              <div className="imp-stat is-new">
                <span className="n">{batch.NewCount}</span>
                <span className="l">New</span>
              </div>
              <div className="imp-stat is-warn">
                <span className="n">{batch.MatchedCount}</span>
                <span className="l">Already here</span>
              </div>
              <div className="imp-stat is-warn">
                <span className="n">{batch.DuplicateCount}</span>
                <span className="l">Twice in file</span>
              </div>
              <div className="imp-stat is-bad">
                <span className="n">{batch.InvalidCount}</span>
                <span className="l">Invalid</span>
              </div>
              <div className="imp-stat">
                <span className="n">{willImport}</span>
                <span className="l">Will be created</span>
              </div>
            </div>

            {batch.ImportStatus === 'COMMITTED' ? (
              <div className="banner info">
                <span className="material-symbols-outlined">task_alt</span>
                <div>
                  <b>Committed{batch.CommittedAt ? ` on ${formatShortDate(batch.CommittedAt)}` : ''}.</b>{' '}
                  The contacts this created are ordinary contacts now — editing or removing them is
                  done from their own records, not from here.
                </div>
              </div>
            ) : batch.ImportStatus === 'CANCELLED' ? (
              <div className="banner info">
                <span className="material-symbols-outlined">cancel</span>
                <div><b>Cancelled.</b> Nothing was added to your contacts. Upload the file again to retry.</div>
              </div>
            ) : (
              <div className="banner info">
                <span className="material-symbols-outlined">visibility</span>
                <div>
                  <b>Nothing has been added to your contacts yet.</b> These {batch.TotalRows} rows are
                  staged. Check the column mapping, decide what to do with anything already here,
                  then commit.
                </div>
              </div>
            )}

            {/* ---------------------------------------------- column mapping */}
            {reviewable && (
              <div className="card" style={{ marginBottom: 14 }}>
                <div className="card-head">
                  <span className="card-title">
                    <span className="material-symbols-outlined">swap_horiz</span>Column mapping
                  </span>
                </div>
                <div className="card-body">
                  <div className="imp-map">
                    {batch.Headers.map((header) => (
                      <div key={header} style={{ display: 'contents' }}>
                        <span className="imp-head">{header}</span>
                        <span className="arrow material-symbols-outlined">arrow_forward</span>
                        <select
                          className="form-input"
                          value={batch.Mapping[header] ?? ''}
                          aria-label={`Map column ${header}`}
                          disabled={busy}
                          onChange={(e) => void remap(header, e.target.value)}
                        >
                          <option value="">— ignore this column —</option>
                          {fields.map((f) => (
                            <option key={f} value={f}>{f}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                  <p className="person-sub" style={{ marginTop: 10 }}>
                    Changing a mapping re-checks every row, because the mapping is what decides which
                    rows are duplicates and which are invalid.
                  </p>
                </div>
              </div>
            )}

            {/* ---------------------------------------------- rows */}
            {reviewable && rows.length > 0 && (
              <div className="chip-row" style={{ marginBottom: 10 }}>
                <button
                  className="chip"
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    void setResolutions(
                      Object.fromEntries(
                        rows.filter((r) => r.RowStatus === 'NEW')
                            .map((r) => [r.ImportRowId, 'IMPORT' as ImportResolution]),
                      ),
                    )
                  }
                >
                  Select all new
                </button>
                <button
                  className="chip"
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    void setResolutions(
                      Object.fromEntries(rows.map((r) => [r.ImportRowId, 'SKIP' as ImportResolution])),
                    )
                  }
                >
                  Skip everything
                </button>
              </div>
            )}

            {busy && <Spinner />}

            <div className="table-wrap">
              <div className="table-scroll">
                <table className="dt">
                  <caption className="sr-only">
                    Staged rows with what was found, why, and what will happen to each.
                  </caption>
                  <thead>
                    <tr>
                      <th>Row</th><th>Record</th><th>What we found</th><th>Why</th><th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <ImportRowLine
                        key={row.ImportRowId}
                        row={row}
                        target={batch.Target}
                        editable={Boolean(reviewable) && !busy}
                        onResolve={(resolution) =>
                          void setResolutions({ [row.ImportRowId]: resolution })
                        }
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="table-foot">
                <span>{rows.length} rows · {willImport} will be created</span>
                {reviewable && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn ghost sm" type="button" disabled={busy}
                            onClick={() => setConfirming('cancel')}>
                      Cancel import
                    </button>
                    <button className="btn primary sm" type="button" disabled={busy || willImport === 0}
                            onClick={() => setConfirming('commit')}>
                      <span className="material-symbols-outlined">check</span>
                      Create {willImport} record{willImport === 1 ? '' : 's'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {confirming === 'commit' && batch && (
        <ConfirmDialog
          title={`Create ${willImport} record${willImport === 1 ? '' : 's'}?`}
          message={`This adds them to your contacts. Rows marked Skip are left alone, and nothing already in your contacts is changed. After this the records are ordinary contacts — undoing the import means deleting them one by one.`}
          confirmLabel="Create them"
          onConfirm={() => void commit()}
          onCancel={() => setConfirming(null)}
        />
      )}
      {confirming === 'cancel' && batch && (
        <ConfirmDialog
          title="Cancel this import?"
          message="Nothing has been added to your contacts, so there is nothing to undo. The staged rows are kept as a record that the file was reviewed."
          confirmLabel="Cancel it"
          danger
          onConfirm={() => void cancel()}
          onCancel={() => setConfirming(null)}
        />
      )}
    </>
  )
}

function ImportRowLine({ row, target, editable, onResolve }: {
  row: ImportRow
  target: ImportTarget
  editable: boolean
  onResolve: (resolution: ImportResolution) => void
}) {
  const mapped = row.MappedData ?? {}
  const label =
    target === 'ORGANIZATIONS'
      ? mapped.CompanyName || '—'
      : [mapped.FirstName, mapped.LastName].filter(Boolean).join(' ') ||
        mapped.DisplayName ||
        mapped.PrimaryEmail ||
        '—'
  const secondary =
    target === 'ORGANIZATIONS'
      ? mapped.Website
      : [mapped.PrimaryEmail, mapped.OrganizationName].filter(Boolean).join(' · ')

  return (
    <tr>
      <td><span className="person-sub">{row.RowNumber}</span></td>
      <td>
        <span className="person-name">{label}</span>
        {secondary && <div className="person-sub">{secondary}</div>}
      </td>
      <td>
        <span className={`badge b-${STATUS_TONE[row.RowStatus] ?? 'neutral'}`}>
          <span className="dot" />{row.RowStatus}
        </span>
        <div className="person-sub">{STATUS_MEANING[row.RowStatus]}</div>
      </td>
      <td>
        {row.MatchedContactId ? (
          <Link className="person-sub" to={`/relationships/people/${row.MatchedContactId}`}>
            {row.MatchReason ?? 'Already here'}
          </Link>
        ) : (
          <span className="person-sub">{row.MatchReason ?? '—'}</span>
        )}
        {/* Every problem, not the first one. Fixing them one round trip at a time is how a
            200-row import becomes an afternoon. */}
        {(row.ValidationMessages ?? []).map((m, i) => (
          <div className="imp-row-msg" key={i}>{m}</div>
        ))}
      </td>
      <td>
        {row.CreatedContactId ? (
          <Link className="btn ghost sm" to={`/relationships/people/${row.CreatedContactId}`}>
            Open
          </Link>
        ) : editable ? (
          <select
            className="ie-select"
            value={row.Resolution}
            aria-label={`What to do with row ${row.RowNumber}`}
            disabled={row.RowStatus === 'INVALID'}
            onChange={(e) => onResolve(e.target.value as ImportResolution)}
          >
            <option value="IMPORT">Create</option>
            <option value="SKIP">Skip</option>
          </select>
        ) : (
          <span className="person-sub">{row.Resolution}</span>
        )}
      </td>
    </tr>
  )
}
